import { encode as encodeMsgpack } from '@msgpack/msgpack'
import * as secp from '@noble/secp256k1'
import { hashTypedData, keccak256 } from 'viem'

const toUint64Bytes = (n: bigint | number | string): Uint8Array => {
  const bytes = new Uint8Array(8)
  new DataView(bytes.buffer).setBigUint64(0, BigInt(n))
  return bytes
}

const createL1ActionHash = (args: {
  action: unknown
  nonce: number
  vaultAddress?: `0x${string}`
  expiresAfter?: number
}): `0x${string}` => {
  const { action, nonce, vaultAddress, expiresAfter } = args

  // 1. Action
  const actionBytes = encodeMsgpack(action)

  // 2. Nonce
  const nonceBytes = toUint64Bytes(nonce)

  // 3. Vault address
  const vaultMarker = vaultAddress ? new Uint8Array([1]) : new Uint8Array([0])
  const vaultBytes = vaultAddress ? secp.etc.hexToBytes(vaultAddress.slice(2)) : new Uint8Array()

  // 4. Expires after
  const expiresMarker = expiresAfter !== undefined ? new Uint8Array([0]) : new Uint8Array()
  const expiresBytes = expiresAfter !== undefined ? toUint64Bytes(expiresAfter) : new Uint8Array()

  // Create a hash
  const bytes = secp.etc.concatBytes(
    actionBytes,
    nonceBytes,
    vaultMarker,
    vaultBytes,
    expiresMarker,
    expiresBytes
  )
  const hash = keccak256(bytes)
  return hash
}

export const prepareUserSignedTypedData = (args: {
  action: {
    signatureChainId: `0x${string}`
    [key: string]: unknown
  }
  types: {
    name: string
    type: string
  }[]
  primaryType: string
}) => {
  const {
    action: { signatureChainId },
    types,
    primaryType,
  } = args

  return hashTypedData({
    domain: {
      name: 'HyperliquidSignTransaction',
      version: '1',
      chainId: parseInt(signatureChainId),
      verifyingContract: '0x0000000000000000000000000000000000000000',
    },
    types: { [primaryType]: types },
    primaryType,
    message: args.action,
  })
}

export const prepareHyperliquidSignature = (action: unknown, nonce: number) => {
  const hash = createL1ActionHash({ action, nonce })

  return hashTypedData({
    domain: {
      name: 'Exchange',
      version: '1',
      chainId: 1337,
      verifyingContract: '0x0000000000000000000000000000000000000000',
    },
    types: {
      Agent: [
        { name: 'source', type: 'string' },
        { name: 'connectionId', type: 'bytes32' },
      ],
    },
    primaryType: 'Agent',
    message: { source: 'a', connectionId: hash },
  })
}
